import { useEffect, useState } from "react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { usePaged } from "../../lib/usePaged.js";
import { useLookup, useLookupRefresh } from "../../context/LookupContext.js";
import { POLICY_STATUS_EVENT } from "../../lib/policySigning.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";
import { FileInput } from "../../components/ui/file-input.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Pagination } from "../../components/ui/pagination.js";
import { SelectField } from "../../components/ui/select-field.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";

interface PolicyVersion {
  id: string; kind: string; title: string; version: string;
  sourceMimeType: string | null; processedAt: string | null;
  /** Claude read the text, so employees see it as formatted headings and clauses. */
  hasContent: boolean;
  /** Reading the text: PENDING, READY or FAILED (contentError says why); null for older uploads. */
  contentStatus: "PENDING" | "READY" | "FAILED" | null; contentError: string | null;
  /** Every employee signs it (on next login, and every 1 January); otherwise only those HR ticks. */
  signAll: boolean;
  isActive: boolean; publishedAt: string | null; createdAt: string;
}

const NEW_TYPE = "__new__";
const isPdf = (f: File) => /\.pdf$/i.test(f.name) && (!f.type || f.type === "application/pdf");
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN");

/**
 * Admin → Policy Documents: the documents employees sign (Employee Handbook,
 * Leave Policy, and any type HR names here). Each type keeps its versions;
 * publishing one makes it the version employees read and sign. Uploads are
 * PDF only; Claude reads the text for the signing screen. Each upload says who
 * signs it: every employee (on their next login, and again every 1 January),
 * or only the employees HR ticks on their profile.
 */
export default function PolicyDocumentsPanel() {
  const types = useLookup("POLICY_DOCUMENT");
  const refreshLookup = useLookupRefresh();
  const confirm = useConfirm();
  const typeName = (kind: string) => types.find(t => t.value === kind)?.displayName ?? kind;

  const [versions, setVersions] = useState<PolicyVersion[] | null>(null);
  const [filter, setFilter] = useState("ALL");
  const [form, setForm] = useState({ kind: "HANDBOOK", newName: "", title: "", version: "" });
  const [file, setFile] = useState<File | null>(null);
  /** Yes / No: must every employee sign this version? Nothing is chosen until HR picks. */
  const [signAll, setSignAll] = useState<boolean | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [readingId, setReadingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    try {
      const r = await api.get<PolicyVersion[]>("/handbooks");
      setVersions(r.data);
    } catch (e) {
      logError("PolicyDocumentsPanel.load", e);
      setError(errMsg(e) ?? "Could not load the documents.");
    }
  };
  useEffect(() => { void load(); }, []);

  // The text is read in the background after upload; refresh until it is done.
  // One still pending after 15 minutes lost its reader (e.g. a server restart).
  const isReading = (v: PolicyVersion) => v.contentStatus === "PENDING" && !!v.processedAt && Date.now() - new Date(v.processedAt).getTime() < 15 * 60 * 1000;
  const reading = (versions ?? []).some(isReading);
  useEffect(() => {
    if (!reading) return;
    const t = window.setInterval(() => { void load(); }, 5000);
    return () => window.clearInterval(t);
  }, [reading]);

  const shown = (versions ?? []).filter(v => filter === "ALL" || v.kind === filter);
  const paged = usePaged(shown, `policy-docs|${filter}`, 25);
  const isNew = form.kind === NEW_TYPE;
  const ready = !!file && !!form.title.trim() && !!form.version.trim() && (!isNew || form.newName.trim().length >= 2) && signAll !== null;

  // The picker filters to PDFs, but its "All files" option lets anything through:
  // refuse other files as soon as they are picked.
  function pickFile(f: File | null) {
    if (f && !isPdf(f)) {
      setFile(null); setSuccess("");
      setError(`"${f.name}" is not a PDF. Upload a PDF (.pdf) file; save Word or Excel files as PDF first.`);
      return;
    }
    setError("");
    setFile(f);
  }

  async function upload() {
    if (!file || !ready) throw new Error("validation");
    if (!isPdf(file)) {
      setError("Upload a PDF (.pdf) file. Save a Word document as PDF first.");
      throw new Error("validation");
    }
    setError(""); setSuccess("");
    try {
      const { data } = await api.post<{ handbook: PolicyVersion; uploadUrl: string; contentType: string }>("/handbooks", {
        ...(isNew ? { newName: form.newName.trim() } : { kind: form.kind }),
        title: form.title.trim(), version: form.version.trim(), filename: file.name, signAll,
      });
      const put = await fetch(data.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": data.contentType } });
      if (!put.ok) throw new Error("The file could not be stored. Please try again.");
      // Checks the PDF and has Claude read its text into headings and clauses.
      await api.post(`/handbooks/${data.handbook.id}/process`, {});
      if (isNew) refreshLookup();
      setSuccess(`${isNew ? form.newName.trim() : typeName(form.kind)} v${form.version.trim()} uploaded. Its text is being read (this can take a minute); publish it below when ready.`);
      setForm(f => ({ kind: isNew ? data.handbook.kind : f.kind, newName: "", title: "", version: "" }));
      setFile(null);
      setSignAll(null);
      await load();
    } catch (e) {
      logError("PolicyDocumentsPanel.upload", e);
      setError(errMsg(e) ?? (e instanceof Error ? e.message : "Upload failed"));
      await load();
      throw e;
    }
  }

  // Reads the text again: after a failure, or for a version uploaded before this existed.
  async function readText(v: PolicyVersion) {
    setReadingId(v.id); setError(""); setSuccess("");
    try {
      await api.post(`/handbooks/${v.id}/process`, {});
      setSuccess(`${v.title} v${v.version}: reading the text (this can take a minute).`);
      await load();
    } catch (e) {
      logError("PolicyDocumentsPanel.readText", e);
      setError(errMsg(e) ?? "Could not read the text.");
    } finally {
      setReadingId(null);
    }
  }

  async function publish(v: PolicyVersion) {
    if (v.signAll) {
      const ok = await confirm({
        title: `Publish and ask every employee to sign?`,
        description: `Every employee will be asked to sign ${v.title} v${v.version} on their next login, and again every 1 January.`,
        actionText: "Publish",
        tone: "warning",
      });
      if (!ok) return;
    }
    setPublishingId(v.id); setError(""); setSuccess("");
    try {
      await api.patch(`/handbooks/${v.id}/publish`, {});
      setSuccess(v.signAll ? "Published — every employee will sign it on their next login." : "Published — employees ticked on their profile will sign it.");
      // The publisher is an employee too: their own sign-off pop-up opens now.
      window.dispatchEvent(new Event(POLICY_STATUS_EVENT));
      await load();
    } catch (e) {
      logError("PolicyDocumentsPanel.publish", e);
      setError(errMsg(e) ?? "Could not publish.");
    } finally {
      setPublishingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Upload a new version</CardTitle>
          <CardDescription>
            PDF only. Its text is read into headings, numbered clauses and tables for employees to read. Publish a version to make it the one employees read and sign.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Document, then (for a new type) its name, then title and version. */}
          <div className={isNew ? "grid gap-3 md:grid-cols-[200px_1fr_1fr_140px]" : "grid gap-3 md:grid-cols-[200px_1fr_140px]"}>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label>Document *</Label>
              <SelectField value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}>
                {types.map(t => <option key={t.value} value={t.value}>{t.displayName}</option>)}
                <option value={NEW_TYPE}>+ New document…</option>
              </SelectField>
            </div>
            {isNew && (
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="policy-new-name">New document name *</Label>
                <Input id="policy-new-name" value={form.newName} onChange={e => setForm(f => ({ ...f, newName: e.target.value }))}
                  placeholder="e.g. Code of Conduct" maxLength={100} autoFocus />
                <span className="text-[11px] text-surface-500">Added to the Document list for future uploads.</span>
              </div>
            )}
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="policy-title">Title *</Label>
              <Input id="policy-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder={isNew ? "e.g. Code of Conduct" : `e.g. ${typeName(form.kind)}`} maxLength={200} />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="policy-version">Version *</Label>
              <Input id="policy-version" value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="e.g. 2026.1" maxLength={20} />
            </div>
          </div>
          <div className="flex max-w-md flex-col gap-1.5">
            <Label>File (PDF) *</Label>
            <FileInput accept=".pdf,application/pdf" value={file} onChange={pickFile} />
          </div>
          {/* Yes / No, one of the two required: two checkboxes acting as one choice. */}
          <div className="flex flex-col gap-1.5">
            <Label id="policy-sign-all">All employees must sign this version? *</Label>
            <div role="group" aria-labelledby="policy-sign-all" className="flex items-center gap-5">
              {([true, false] as const).map(value => (
                <label key={String(value)} className="flex items-center gap-2 text-[13px] text-surface-800">
                  <Checkbox checked={signAll === value}
                    onCheckedChange={(c) => setSignAll(c === true ? value : null)} />
                  {value ? "Yes" : "No"}
                </label>
              ))}
            </div>
            <span className="text-[11px] text-surface-500">
              Yes: every employee signs it on their next login, and again every 1 January. No: only employees you tick on their profile sign it.
            </span>
          </div>
          <AsyncButton onClick={() => upload()} disabled={!ready}>Upload</AsyncButton>
        </CardContent>
      </Card>

      {error && <p role="alert" className="text-sm text-[var(--color-error-fg)]">{error}</p>}
      {success && <p className="text-sm text-[var(--color-success-fg)]">{success}</p>}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-[15px]">Versions</CardTitle>
            <SelectField value={filter} onChange={e => setFilter(e.target.value)} className="w-56" size="sm">
              <option value="ALL">All documents</option>
              {types.map(t => <option key={t.value} value={t.value}>{t.displayName}</option>)}
            </SelectField>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {versions === null ? (
            <p className="p-6 text-sm text-surface-500">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="p-6 text-sm text-surface-500">No versions uploaded yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Signed by</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-px text-right whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.pageItems.map(v => (
                  <TableRow key={v.id}>
                    <TableCell>{typeName(v.kind)}</TableCell>
                    <TableCell className="font-medium">{v.title}</TableCell>
                    <TableCell>{v.version}</TableCell>
                    <TableCell className="text-surface-500">
                      {v.sourceMimeType?.includes("word") ? "Word → PDF" : "PDF"}
                      {v.processedAt && (
                        <span className="block text-[11px]" title={v.contentError ?? undefined}>
                          {isReading(v) ? "Reading text…"
                            : v.hasContent ? "Text extracted"
                            : v.contentStatus === "FAILED" ? "Text not read · shown as PDF"
                            : "Shown as PDF"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {v.signAll ? <Badge tone="info">All employees · yearly</Badge> : <Badge tone="neutral">Selected employees</Badge>}
                    </TableCell>
                    <TableCell>
                      {!v.processedAt ? <Badge tone="error">Not processed</Badge>
                        : !v.publishedAt ? <Badge tone="warning">Draft</Badge>
                        : v.isActive ? <Badge tone="success">Published (active)</Badge>
                        : <Badge tone="neutral">Published {fmtDate(v.publishedAt)}</Badge>}
                    </TableCell>
                    <TableCell className="text-surface-500">{fmtDate(v.createdAt)}</TableCell>
                    <TableCell className="w-px whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        {v.processedAt && !isReading(v) && (
                          <Button size="xs" variant="outline" onClick={() => void readText(v)} disabled={readingId !== null}
                            title={v.hasContent ? "Read the text again, e.g. to pick up tables" : "Read the document's text"}>
                            {v.hasContent ? "Read again" : "Read text"}
                          </Button>
                        )}
                        {!v.publishedAt && v.processedAt && (
                          <Button size="xs" onClick={() => void publish(v)} disabled={publishingId === v.id || (!!v.sourceMimeType?.includes("word") && isReading(v))}>Publish</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Pagination meta={paged.meta} onPageChange={paged.setPage} />
    </div>
  );
}
